import {
  ChangeDetectionStrategy, Component, ElementRef, HostListener,
  OnDestroy, ViewChild, effect, signal,
} from '@angular/core';
import { TourStep } from '../../models/guided-tour.model';
import { GuidedTourService } from '../../services/guided-tour.service';

interface SpotRect { top: number; left: number; width: number; height: number; }
interface TipPos { top: number; left: number; }

/** Breathing room between the target's bounding box and the spotlight ring. */
const SPOT_PADDING = 6;
/** Gap between the spotlight ring and the tooltip card. */
const TIP_GAP = 14;
/** Minimum distance from the viewport edge for the tooltip. */
const EDGE_MARGIN = 12;

/**
 * Renders the active tour from GuidedTourService: a dimming layer with a
 * spotlight ring over the current step's [data-tour] target, plus a tooltip
 * card with Back / Next / Skip. Steps without an anchor render as a centered
 * card on a plain dim. Mounted once in the layout shell.
 *
 * Positioning is measured, not estimated: the card renders invisibly first,
 * then a rAF pass reads its real size and places it on the side with room
 * (honoring the step's preferred placement), clamped to the viewport.
 */
@Component({
  selector: 'app-guided-tour',
  standalone: true,
  templateUrl: './guided-tour.component.html',
  styleUrls: ['./guided-tour.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuidedTourComponent implements OnDestroy {
  @ViewChild('tipCard') private tipCard?: ElementRef<HTMLElement>;

  /** Spotlight rect in viewport px; null = centered (anchorless) step. */
  readonly spot = signal<SpotRect | null>(null);
  readonly tipPos = signal<TipPos | null>(null);
  /** Hides the card until the measuring pass has placed it (first step only). */
  readonly placed = signal(false);

  // Recompute when a scroll anywhere (e.g. the sidebar's session list) moves
  // the target. Registered on document with capture so nested scrollers count.
  private readonly onAnyScroll = () => this.reposition();

  constructor(readonly tour: GuidedTourService) {
    effect(() => {
      const step = this.tour.current();
      if (!step) {
        this.spot.set(null);
        this.tipPos.set(null);
        this.placed.set(false);
        return;
      }
      // Wait a frame so the card for the new step exists before measuring.
      requestAnimationFrame(() => this.position(step));
    });
    document.addEventListener('scroll', this.onAnyScroll, true);
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.onAnyScroll, true);
  }

  @HostListener('window:resize')
  onResize(): void { this.reposition(); }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.tour.active()) return;
    switch (event.key) {
      case 'Escape': this.tour.skip(); break;
      case 'ArrowRight':
      case 'Enter': this.tour.next(); break;
      case 'ArrowLeft': this.tour.back(); break;
      default: return;
    }
    event.preventDefault();
  }

  private reposition(): void {
    const step = this.tour.current();
    if (step) this.position(step);
  }

  private position(step: TourStep): void {
    const el = step.anchor
      ? document.querySelector<HTMLElement>(`[data-tour="${step.anchor}"]`)
      : null;
    if (!el) {
      // Anchorless by design, or the target vanished mid-tour: centered card.
      this.spot.set(null);
      this.tipPos.set(null);
      this.placed.set(true);
      return;
    }
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    const r = el.getBoundingClientRect();
    this.spot.set({
      top: r.top - SPOT_PADDING,
      left: r.left - SPOT_PADDING,
      width: r.width + SPOT_PADDING * 2,
      height: r.height + SPOT_PADDING * 2,
    });
    requestAnimationFrame(() => this.placeTip(step));
  }

  private placeTip(step: TourStep): void {
    const spot = this.spot();
    const card = this.tipCard?.nativeElement;
    if (!spot || !card) return;
    const tipW = card.offsetWidth;
    const tipH = card.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const fits: Record<string, boolean> = {
      right: spot.left + spot.width + TIP_GAP + tipW <= vw - EDGE_MARGIN,
      left: spot.left - TIP_GAP - tipW >= EDGE_MARGIN,
      bottom: spot.top + spot.height + TIP_GAP + tipH <= vh - EDGE_MARGIN,
      top: spot.top - TIP_GAP - tipH >= EDGE_MARGIN,
    };
    const preferred = step.placement && step.placement !== 'auto' ? [step.placement] : [];
    const side = [...preferred, 'right', 'bottom', 'left', 'top']
      .find(s => fits[s]) ?? 'bottom';

    let top: number;
    let left: number;
    switch (side) {
      case 'right':
        top = spot.top + spot.height / 2 - tipH / 2;
        left = spot.left + spot.width + TIP_GAP;
        break;
      case 'left':
        top = spot.top + spot.height / 2 - tipH / 2;
        left = spot.left - TIP_GAP - tipW;
        break;
      case 'top':
        top = spot.top - TIP_GAP - tipH;
        left = spot.left + spot.width / 2 - tipW / 2;
        break;
      default: // bottom
        top = spot.top + spot.height + TIP_GAP;
        left = spot.left + spot.width / 2 - tipW / 2;
    }
    this.tipPos.set({
      top: Math.max(EDGE_MARGIN, Math.min(top, vh - tipH - EDGE_MARGIN)),
      left: Math.max(EDGE_MARGIN, Math.min(left, vw - tipW - EDGE_MARGIN)),
    });
    this.placed.set(true);
  }
}
